import { Injectable } from '@angular/core';

export interface ErrorMessage {
  title: string;
  summary: string;
  detail?: string;
}

export interface ErrorContext {
  statusCode: number;
  actionType?: string;
  serverMessage?: string;
}

/**
 * Service for mapping HTTP error codes to user-friendly messages
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorMessageService {
  /**
   * Get user-friendly error message based on status code and context
   */
  getErrorMessage(context: ErrorContext): ErrorMessage {
    const { statusCode, actionType, serverMessage } = context;

    // Try to get context-specific message first
    const contextMessage = this.getContextSpecificMessage(statusCode, actionType);
    if (contextMessage) {
      return contextMessage;
    }

    // Fall back to status code mapping
    return this.getStatusCodeMessage(statusCode, serverMessage);
  }

  /**
   * Get context-specific error messages for better UX
   */
  private getContextSpecificMessage(statusCode: number, actionType?: string): ErrorMessage | null {
    if (!actionType) return null;

    const actionLower = actionType.toLowerCase();

    // Authentication contexts (401)
    if (statusCode === 401) {
      if (actionLower.includes('login') || actionLower.includes('authenticate')) {
        return {
          title: 'Login Failed',
          summary: 'Invalid credentials or authentication method.',
          detail:
            'Please check your username and password, or try a different authentication method.',
        };
      }
      if (actionLower.includes('webauthn') || actionLower.includes('assertion')) {
        return {
          title: 'Authentication Failed',
          summary: 'Unable to verify your security key or biometric.',
          detail: 'Please ensure your security device is connected and try again.',
        };
      }
      // Default 401 for other contexts
      return {
        title: 'Session Expired',
        summary: 'Your session has expired.',
        detail: 'Please log in again to continue.',
      };
    }

    // Permission contexts (403)
    if (statusCode === 403) {
      if (actionLower.includes('delete')) {
        return {
          title: 'Delete Not Allowed',
          summary: 'You do not have permission to delete this item.',
          detail: 'Contact your administrator if you need delete access.',
        };
      }
      if (actionLower.includes('update') || actionLower.includes('edit')) {
        return {
          title: 'Edit Not Allowed',
          summary: 'You do not have permission to modify this item.',
          detail: 'This item may be read-only or require higher privileges.',
        };
      }
      if (actionLower.includes('create') || actionLower.includes('add')) {
        return {
          title: 'Create Not Allowed',
          summary: 'You do not have permission to create new items.',
          detail: 'Contact your administrator if you need create access.',
        };
      }
    }

    // Not found contexts (404)
    if (statusCode === 404) {
      if (actionLower.includes('user') || actionLower.includes('account')) {
        return {
          title: 'User Not Found',
          summary: 'The requested user account does not exist.',
          detail: 'The account may have been deleted or the link is incorrect.',
        };
      }
      if (actionLower.includes('load') || actionLower.includes('fetch')) {
        return {
          title: 'Item Not Found',
          summary: 'The requested item could not be found.',
          detail: 'It may have been deleted or moved.',
        };
      }
    }

    return null;
  }

  /**
   * Get generic message based on HTTP status code
   */
  private getStatusCodeMessage(statusCode: number, serverMessage?: string): ErrorMessage {
    const baseMessage = this.getBaseStatusMessage(statusCode);

    // If server provided a user-friendly message, use it for summary
    // Otherwise use our default summary
    const summary = this.isUserFriendlyMessage(serverMessage)
      ? serverMessage!
      : baseMessage.summary;

    return {
      ...baseMessage,
      summary,
    };
  }

  /**
   * Check if server message looks user-friendly (not a raw error)
   */
  private isUserFriendlyMessage(message?: string): boolean {
    if (!message) return false;

    // Exclude raw HTTP error messages
    const technicalPatterns = [
      /^Http failure response/i,
      /^Unknown Error/i,
      /^\d+ \w+$/i, // Like "500 Error"
      /^Error \d+/i,
      /null|undefined/i,
    ];

    return !technicalPatterns.some((pattern) => pattern.test(message));
  }

  /**
   * Base message mapping for HTTP status codes
   */
  private getBaseStatusMessage(statusCode: number): ErrorMessage {
    switch (statusCode) {
      case 400:
        return {
          title: 'Invalid Request',
          summary: 'The request could not be processed.',
          detail: 'Please check your input and try again. Some fields may be invalid.',
        };

      case 401:
        return {
          title: 'Authentication Required',
          summary: 'You need to log in to access this.',
          detail: 'Your session may have expired. Please log in again.',
        };

      case 403:
        return {
          title: 'Access Denied',
          summary: 'You do not have permission to access this.',
          detail:
            'This resource requires additional permissions. Contact your administrator if needed.',
        };

      case 404:
        return {
          title: 'Not Found',
          summary: 'The requested item could not be found.',
          detail: 'The item may have been moved or deleted.',
        };

      case 408:
        return {
          title: 'Request Timeout',
          summary: 'The request took too long to complete.',
          detail: 'Please check your internet connection and try again. The server may be busy.',
        };

      case 409:
        return {
          title: 'Conflict',
          summary: 'This action conflicts with existing data.',
          detail: 'The item may have been modified by someone else. Please refresh and try again.',
        };

      case 422:
        return {
          title: 'Validation Error',
          summary: 'Some of the information provided is invalid.',
          detail: 'Please review your input and correct any errors before trying again.',
        };

      case 429:
        return {
          title: 'Too Many Requests',
          summary: 'You are making requests too quickly.',
          detail:
            'Please wait a moment before trying again. This helps keep the system running smoothly.',
        };

      case 500:
        return {
          title: 'Server Error',
          summary: 'An unexpected error occurred on the server.',
          detail:
            'Our team has been notified. Please try again later or contact support if the problem persists.',
        };

      case 502:
        return {
          title: 'Bad Gateway',
          summary: 'Unable to connect to the server.',
          detail: 'The service may be temporarily unavailable. Please try again in a few moments.',
        };

      case 503:
        return {
          title: 'Service Unavailable',
          summary: 'The service is temporarily unavailable.',
          detail: 'We may be performing maintenance. Please try again in a few minutes.',
        };

      case 504:
        return {
          title: 'Gateway Timeout',
          summary: 'The server took too long to respond.',
          detail:
            'Please try again. If the problem persists, the service may be experiencing high load.',
        };

      case 0:
        return {
          title: 'Connection Error',
          summary: 'Unable to connect to the server.',
          detail: 'Please check your internet connection and ensure the server is accessible.',
        };

      default:
        return {
          title: `Error ${statusCode}`,
          summary: 'An unexpected error occurred.',
          detail: 'Please try again. If the problem persists, contact support.',
        };
    }
  }

  /**
   * Get icon class for error status code
   */
  getErrorIcon(statusCode: number): string {
    if (statusCode >= 500) {
      return 'pi pi-exclamation-triangle'; // Server errors
    } else if (statusCode === 404) {
      return 'pi pi-search'; // Not found
    } else if (statusCode === 401 || statusCode === 403) {
      return 'pi pi-lock'; // Auth errors
    } else if (statusCode === 429 || statusCode === 408) {
      return 'pi pi-clock'; // Rate limiting / Timeout
    } else if (statusCode === 0) {
      return 'pi pi-wifi'; // Connection error
    } else {
      return 'pi pi-times-circle'; // General error
    }
  }

  /**
   * Get severity for error status code
   */
  getSeverity(statusCode: number): 'error' | 'warning' {
    if (statusCode >= 500) return 'error';
    if ([400, 422].includes(statusCode)) return 'error';
    if ([401, 403, 404, 408, 409, 429].includes(statusCode)) return 'warning';
    return 'error';
  }

  /**
   * Check if error code should allow retry
   */
  canRetry(statusCode: number): boolean {
    // Allow retry for temporary errors
    return [408, 429, 500, 502, 503, 504].includes(statusCode);
  }
}
